import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { markOAuthRedirectPending } from './completeOAuthRedirect';
import { shouldUseOAuthRedirect } from './oauthEnv';

export type ExtraProvider = 'microsoft' | 'facebook';

/**
 * Website-only providers, each switched on once it is enabled in Firebase
 * (Authentication → Sign-in method) and its own developer console:
 *   VITE_MICROSOFT_SIGNIN=true  — Azure app registration
 *   VITE_FACEBOOK_SIGNIN=true   — Meta for Developers app
 * The native app would need extra SDK setup, so they stay web-only.
 */
export const extraProvidersEnabled = (): ExtraProvider[] => {
  if (Capacitor.isNativePlatform()) return [];
  const out: ExtraProvider[] = [];
  if (import.meta.env.VITE_MICROSOFT_SIGNIN === 'true') out.push('microsoft');
  if (import.meta.env.VITE_FACEBOOK_SIGNIN === 'true') out.push('facebook');
  return out;
};

/** Same popup/redirect split as google.ts; returns null when the page redirected away. */
export const signInWithExtraProvider = async (provider: ExtraProvider): Promise<string | null> => {
  const call =
    provider === 'microsoft'
      ? FirebaseAuthentication.signInWithMicrosoft.bind(FirebaseAuthentication)
      : FirebaseAuthentication.signInWithFacebook.bind(FirebaseAuthentication);
  if (shouldUseOAuthRedirect()) {
    markOAuthRedirectPending(provider);
    await call({ mode: 'redirect' });
    return null;
  }
  await call({ mode: 'popup' });
  const { token } = await FirebaseAuthentication.getIdToken();
  if (!token) throw new Error(`No idToken returned from ${provider} sign-in`);
  return token;
};
